import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPin, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { api, ApiError } from '../lib/api';
import { SelectionBar } from '../components/SelectionBar';
import { DataTable, type Column } from '../components/DataTable';
import { useSession } from '../lib/session';
import type { AreaOption } from '../lib/types';

export function AreasPage() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', zoneId: '', travelMinFromTown: '' });
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const { can } = useSession();
  const mayEdit = can('area:write');
  const queryClient = useQueryClient();
  const zones = useQuery({ queryKey: ['zones'], queryFn: api.zones });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['areas'] });
    void queryClient.invalidateQueries({ queryKey: ['zones'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.addArea({
        name: draft.name.trim(),
        zoneId: draft.zoneId,
        // Left blank, the zone's own default stands. Sending 0 would claim the
        // area is in town.
        ...(draft.travelMinFromTown.trim()
          ? { travelMinFromTown: Number(draft.travelMinFromTown) }
          : {}),
      }),
    onSuccess: (area) => {
      setAdding(false);
      setDraft({ name: '', zoneId: '', travelMinFromTown: '' });
      setProblem(null);
      setOutcome(`${area.name} added to ${area.zoneCode}.`);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not add that area.'),
  });

  /*
   * Removing an area is two different actions and the server decides which.
   *
   * One nobody has used is deleted; one named on old jobs is retired, because
   * the row is what those jobs mean by that place. The message comes back from
   * the server rather than being guessed here, so the screen cannot claim a
   * deletion that did not happen.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.removeArea(id),
    onSuccess: (result) => {
      setProblem(null);
      setOutcome(result.message);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not remove that area.'),
  });

  /*
   * Searched and paged on the server, not filtered in the browser.
   *
   * This screen used to fetch every area and filter the array. That is fine at
   * forty areas and quietly wrong at four thousand — the browser holds them
   * all, and a list of chips has no bottom. The picker on a ticket form still
   * fetches them whole, deliberately, because a dropdown that hides options is
   * worse than a long one; this is the screen somebody reads, so it pages.
   */
  const areas = useQuery({
    queryKey: ['areas', 'page', query, page],
    queryFn: () => api.areaPage({ q: query || undefined, page }),
    placeholderData: (previous) => previous,
  });

  if (zones.isPending || areas.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const search = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  const columns: Column<AreaOption>[] = [
    {
      key: 'name',
      sortBy: (area) => area.name.toLowerCase(),
      header: 'Area',
      cell: (area) => (
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {area.name}
        </span>
      ),
    },
    { key: 'zone', header: 'Zone', sortBy: (area) => area.zoneCode, cell: (area) => area.zoneCode },
    {
      key: 'zoneName',
      header: 'Called',
      secondary: true,
      sortBy: (area) => area.zoneName,
      cell: (area) => area.zoneName,
    },
    {
      key: 'travel',
      // On the minutes, so "furthest out" is one click rather than a squint.
      sortBy: (area) => area.travelMin,
      header: 'From town',
      numeric: true,
      secondary: true,
      cell: (area) => (area.travelMin != null ? `~${area.travelMin} min` : '—'),
    },
    ...(mayEdit
      ? [
          {
            key: 'remove',
            header: '',
            cell: (area: AreaOption) => (
              <button
                type="button"
                className="btn-ghost text-danger-600"
                title={`Remove ${area.name}`}
                disabled={remove.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Remove ${area.name}?\n\nIf any job, site, lead or network element is ` +
                        'filed in it, it is retired instead of deleted — it stops being offered ' +
                        'and stays readable on those records.',
                    )
                  ) {
                    remove.mutate(area.id);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Remove {area.name}</span>
              </button>
            ),
          } satisfies Column<AreaOption>,
        ]
      : []),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Areas &amp; zones
            <HelpHint>
              The dispatcher picks an area, which sets a zone, which filters the technician list.
              Technicians cover zones, not individual areas.
            </HelpHint>
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {areas.data?.total ?? 0} areas across {zones.data?.zones.length} zones
            {!mayEdit && ' · read-only for your role'}
          </p>
        </div>

        {mayEdit && !adding && (
          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add an area
          </button>
        )}
      </div>

      {outcome && (
        <p className="card flex items-start justify-between gap-3 border-secondary-300 bg-secondary-50 text-sm text-secondary-900">
          <span>{outcome}</span>
          <button type="button" onClick={() => setOutcome(null)} aria-label="Dismiss">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </p>
      )}

      {adding && (
        <section className="card space-y-3">
          <h3 className="font-heading text-lg">Add an area</h3>
          <p className="text-sm text-muted-foreground">
            A neighbourhood this network covers. The zone it belongs to is what decides which
            technicians can be sent, so it is chosen from the list rather than typed.
          </p>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Name</span>
              <input
                className="input w-full"
                value={draft.name}
                onChange={(event) => setDraft((old) => ({ ...old, name: event.target.value }))}
                placeholder="Nyabururu"
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Dispatch zone</span>
              <select
                className="input w-full"
                value={draft.zoneId}
                onChange={(event) => setDraft((old) => ({ ...old, zoneId: event.target.value }))}
              >
                <option value="">Choose a zone…</option>
                {zones.data?.zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.code} — {zone.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Minutes from town</span>
              <input
                className="input w-full"
                type="number"
                inputMode="numeric"
                min={0}
                value={draft.travelMinFromTown}
                onChange={(event) =>
                  setDraft((old) => ({ ...old, travelMinFromTown: event.target.value }))
                }
                placeholder="the zone's default"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Optional. Left blank, the zone's own travel time is used.
              </span>
            </label>
          </div>

          {problem && (
            <p className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">{problem}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={draft.name.trim().length < 2 || !draft.zoneId || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Plus className="h-4 w-4" aria-hidden="true" />
              )}
              Add it
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setAdding(false);
                setProblem(null);
              }}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {problem && !adding && (
        <p className="card border-accent-300 bg-accent-50 text-sm text-accent-900">{problem}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {zones.data?.zones.map((zone) => (
          <div key={zone.id} className="card">
            <div className="flex items-center justify-between">
              <h3 className="font-heading">
                <span className="text-primary-600">{zone.code}</span> {zone.name}
              </h3>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {zone.areaCount} areas · ~{zone.travelMin} min from town
            </p>
          </div>
        ))}
      </div>

      <input
        type="search"
        value={query}
        onChange={(event) => search(event.target.value)}
        placeholder="Search areas…"
        aria-label="Search areas"
        className="input max-w-sm"
      />

      <SelectionBar
        rows={areas.data?.areas ?? []}
        selected={selected}
        onClear={() => setSelected(new Set())}
        rowKey={(area) => area.id}
        exportName="areas"
        csv={[
          { header: 'Area', value: (a) => a.name },
          { header: 'Zone', value: (a) => a.zoneCode },
          { header: 'Zone name', value: (a) => a.zoneName },
          { header: 'Travel (min from town)', value: (a) => a.travelMin },
        ]}
      />

      <DataTable
        selected={selected}
        onSelect={setSelected}
        rows={areas.data?.areas ?? []}
        columns={columns}
        rowKey={(area) => area.id}
        caption="Areas"
        page={
          areas.data
            ? {
                page: areas.data.page,
                perPage: areas.data.perPage,
                total: areas.data.total,
                pages: areas.data.pages,
              }
            : undefined
        }
        onPage={setPage}
        empty={
          <p className="py-6 text-center text-sm text-muted-foreground">
            {query ? `No area matches “${query}”.` : 'No areas yet.'}
          </p>
        }
      />
    </div>
  );
}
